import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";

export type HubEntityScope = "none" | "own" | "all";

export type HubEntityPermissionGrant = Readonly<{
  canEdit: boolean;
  canSubmit: boolean;
}>;

export type HubEntityPermissions = Readonly<{
  view: HubEntityScope;
  edit: HubEntityScope;
  submit: HubEntityScope;
  additionalEntityPermissions: Readonly<Record<string, HubEntityPermissionGrant>>;
}>;

export type HubPermissions = Readonly<{
  gameConcepts: HubEntityPermissions;
  pitches: HubEntityPermissions;
  canAccessAdministratorSettings: boolean;
  canEditOwnContactDetails: boolean;
  canEditStudioDetails: boolean;
  canChangePrimarySignatory: boolean;
  canSubmitIdApplication: boolean;
  canSubmitPCEnrollment: boolean;
}>;

type HubPermissionLabels = Readonly<{
  manageAllSubmissions: string;
  manageCompanyDetails: string;
  manageHubSettings: string;
  manageOwnSubmissions: string;
}>;

export type HubEntityAction = "view" | "edit" | "submit";

const entityPermissions = (scope: HubEntityScope): HubEntityPermissions => ({
  view: scope,
  edit: scope,
  submit: scope,
  additionalEntityPermissions: {},
});

export const parseHubAccessLevel = (accessLevel: unknown): HubAccessLevel | undefined =>
  accessLevel === HubAccessLevel.Admin ||
  accessLevel === HubAccessLevel.GameManager ||
  accessLevel === HubAccessLevel.User
    ? accessLevel
    : undefined;

export const mapHubAccessLevelToPermissions = (
  accessLevel: HubAccessLevel | undefined
): HubPermissions => {
  const effectiveAccessLevel = accessLevel ?? HubAccessLevel.User;
  const canManageAllGameConcepts =
    effectiveAccessLevel === HubAccessLevel.Admin ||
    effectiveAccessLevel === HubAccessLevel.GameManager;
  const isAdmin = effectiveAccessLevel === HubAccessLevel.Admin;
  const entityScope = canManageAllGameConcepts ? "all" : "own";

  return {
    gameConcepts: entityPermissions(entityScope),
    pitches: entityPermissions(entityScope),
    canAccessAdministratorSettings: isAdmin,
    canEditOwnContactDetails: true,
    canEditStudioDetails: isAdmin,
    canChangePrimarySignatory: isAdmin,
    canSubmitIdApplication: isAdmin,
    canSubmitPCEnrollment: isAdmin,
  };
};

export const getHubAccessPermissionLabels = (
  accessLevel: HubAccessLevel,
  labels: HubPermissionLabels
): string[] => {
  const permissions = mapHubAccessLevelToPermissions(accessLevel);
  return [
    permissions.gameConcepts.view === "all"
      ? labels.manageAllSubmissions
      : labels.manageOwnSubmissions,
    ...(permissions.canEditStudioDetails ? [labels.manageCompanyDetails] : []),
    ...(permissions.canAccessAdministratorSettings ? [labels.manageHubSettings] : []),
  ];
};
